import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ValidationHandlerService, TranslatedToastrService, ImgWithSrcComponentFixWidth75px, EditComponent, BaseComponent, LanguageService, SearchData, DataTableRequest, OneColumnSearchData, StringListItemModelListBaseResponse, EditCouponsComponent } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, CoreService, PartnerModel, UserModel, VestraService } from 'src/app/services/api-services';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { PartnerEditComponent } from '../partner-edit/partner-edit.component';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ADTColumns, ADTSettings } from 'angular-datatables/src/models/settings';
import { Router } from '@angular/router';

@Component({
    selector: 'app-partner-list',
    templateUrl: './partner-list.component.html',
    styleUrls: ['./partner-list.component.scss'],
    standalone: false,
})
export class PartnerListComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('actionsRef') actionsRef: TemplateRef<EditCouponsComponent>;
    @ViewChild('imgWidth75pxRef_brand') imgWidth75pxRef_brand: TemplateRef<ImgWithSrcComponentFixWidth75px>;
    @ViewChild('imgWidth75pxRef_cover') imgWidth75pxRef_cover: TemplateRef<ImgWithSrcComponentFixWidth75px>;

    constructor(
        protected override validationHandlerService: ValidationHandlerService,
        private translatedToastrService: TranslatedToastrService,
        private translateService: TranslateService,
        protected override modalService: BsModalService,
        private vestraService: VestraService,
        private authenticationService: AuthenticationService,
        protected override spinnerService: NgxSpinnerService,
        protected override languageService: LanguageService,
        private coreService: CoreService,
        private router: Router
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();
        const columns = this.getColumns();

        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: OneColumnSearchData = {
                    propertyValue: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.vestraService.getPartnerListDataSource(model)))
                    .then(res => {
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: columns,
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
        };

        return res;
    }

    public ngOnInit(): void {
        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }

    public onAddItem() {
        this.openEditModalWithComponent(null);
    }

    public onEditItem(event: any) {
        this.openEditModalWithComponent(event.data);
    }

    public onEditOrCoupons(event: { action: string; data: any }) {
        if (event.action === 'edit') {
            this.onEditItem(event);
        } else if (event.action === 'coupon') {
            this.navigateToCouponPage(event);
        }
    }
    public navigateToCouponPage(event: any) {
        this.router.navigate([`/administration/coupon-list/${event.data.id}`]);
    }

    public openEditModalWithComponent(item: PartnerModel) {
        let modalOptions: ModalOptions = this.getDefaultModalOptions('partner.list', item);

        let bsModalRef: BsModalRef = this.modalService.show(PartnerEditComponent, modalOptions);
        bsModalRef.onHide.subscribe((reason: string | any) => {
            if (reason === 'save_success') {
                this.translatedToastrService.success('common.save_success', 'common.success');
            }
            this.refreshDataTable();
        });
    }

    private getColumns(): ADTColumns[] {
        let translateService = this.translateService;
        let columns: ADTColumns[] = [
            {
                data: 'name',
                title: 'partner.name',
                searchable: false,
                orderable: true,
            },
            {
                data: 'email',
                title: 'partner.email',
                searchable: false,
                orderable: true,
            },
            {
                data: 'phoneNumber',
                title: 'partner.phone_number',
                searchable: false,
                orderable: true,
            },
            {
                data: 'websiteUrl',
                title: 'partner.website_url',
                searchable: false,
                orderable: true,
            },
            {
                data: 'brandImageUrl',
                title: 'partner.brand_image',
                responsivePriority: 1,
                searchable: false,
                orderable: false,
                ngTemplateRef: {
                    ref: this.imgWidth75pxRef_brand,
                },
            },
            {
                data: 'coverImageUrl',
                title: 'partner.cover_image',
                responsivePriority: 1,
                searchable: false,
                orderable: false,
                ngTemplateRef: {
                    ref: this.imgWidth75pxRef_cover,
                },
            },
        ];

        let actionColumn: ADTColumns = {
            title: 'common.actions',
            data: null,
            searchable: false,
            orderable: false,
            defaultContent: '',
            ngTemplateRef: {
                ref: this.actionsRef,
                context: {
                    captureEvents: this.onEditOrCoupons.bind(this),
                },
            },
            responsivePriority: 1,
        };

        const responsiveTableColumn: ADTColumns = {
            data: '',
            orderable: false,
            responsivePriority: 1,
            render: function (data) {
                return '';
            },
        };

        columns.push(actionColumn);

        columns.push(responsiveTableColumn);

        return columns;
    }
}
